/** Query keys for in-app guided common-task workflows (Training Plans V2). */
export const GUIDED_TASK_QUERY = {
  flow: "gt",
  step: "gs",
};

/**
 * @param {string} search - `location.search`
 * @returns {{ flowId: string, stepIndex: number } | null}
 */
export function parseGuidedTask(search) {
  const params = new URLSearchParams(search || "");
  const flowId = params.get(GUIDED_TASK_QUERY.flow) || "";
  const rawStep = params.get(GUIDED_TASK_QUERY.step);
  if (!flowId || rawStep == null || rawStep === "") return null;
  const stepIndex = Number.parseInt(rawStep, 10);
  if (!Number.isFinite(stepIndex) || stepIndex < 0) return null;
  return { flowId, stepIndex };
}

/**
 * Merge guided params into a destination URL string. Strips old gt/gs from `fromSearch` then applies.
 * @param {string} destPath - pathname only, e.g. /home/training-plans-v2/library
 * @param {string} [destSearch] - query string without leading ? (optional)
 * @param {string} [fromSearch] - current location.search to preserve unrelated params (optional)
 * @param {{ flowId: string, stepIndex: number }} guided
 */
export function mergeGuidedIntoDestination(destPath, destSearch, fromSearch, guided) {
  const next = new URLSearchParams();
  if (fromSearch) {
    const cur = new URLSearchParams(fromSearch);
    cur.forEach((value, key) => {
      if (key !== GUIDED_TASK_QUERY.flow && key !== GUIDED_TASK_QUERY.step) {
        next.set(key, value);
      }
    });
  }
  if (destSearch) {
    const dest = new URLSearchParams(destSearch);
    dest.forEach((value, key) => {
      next.set(key, value);
    });
  }
  const isNewPlanEditor = /\/training-plans-v2\/new(\/|$)/.test(destPath);
  if (!isNewPlanEditor) {
    next.delete("type");
  }
  next.set(GUIDED_TASK_QUERY.flow, guided.flowId);
  next.set(GUIDED_TASK_QUERY.step, String(guided.stepIndex));
  const qs = next.toString();
  return qs ? `${destPath}?${qs}` : destPath;
}

/**
 * Current location with only guided params updated (same pathname).
 */
export function mergeGuidedOnCurrentPath(pathname, search, guided) {
  const params = new URLSearchParams(search || "");
  params.set(GUIDED_TASK_QUERY.flow, guided.flowId);
  params.set(GUIDED_TASK_QUERY.step, String(guided.stepIndex));
  const qs = params.toString();
  return `${pathname}${qs ? `?${qs}` : ""}`;
}

export function stripGuidedParams(search) {
  const params = new URLSearchParams(search || "");
  params.delete(GUIDED_TASK_QUERY.flow);
  params.delete(GUIDED_TASK_QUERY.step);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Keeps guided task params (gt/gs) on internal Training Plans navigations.
 * Use whenever calling `navigate()` to a new path that would otherwise drop `location.search`.
 *
 * @param {string} destPath - Path with optional `?query` (e.g. `/home/.../new?type=template`)
 * @param {string} currentSearch - `location.search` from the screen that triggers navigation
 */
export function withGuidedSearchPreserve(destPath, currentSearch) {
  const guided = parseGuidedTask(currentSearch);
  if (!guided) return destPath;
  const qIndex = destPath.indexOf("?");
  const pathPart = qIndex >= 0 ? destPath.slice(0, qIndex) : destPath;
  const existingQuery = qIndex >= 0 ? destPath.slice(qIndex + 1) : "";
  const params = new URLSearchParams(existingQuery);
  params.set(GUIDED_TASK_QUERY.flow, guided.flowId);
  params.set(GUIDED_TASK_QUERY.step, String(guided.stepIndex));
  const qs = params.toString();
  return `${pathPart}?${qs}`;
}
